import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Typography,
} from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import useMpin from './useMpin';
import useMpinStyles from './useMpinStyles';
import { firstNameOf, initialsOf } from './mpin.helper';
import {
    ExitMpinDialog,
    ForgotMpinDialog,
    LockoutPanel,
    MpinBanner,
    MpinCellInput,
    MpinSuccessTick,
} from './utility.component.mpin';

/**
 * The MPIN gate page (/mpin, BlankLayout). One page, three server-driven
 * variants (setup / verify / change) rendered from the MPIN_VARIANTS
 * config. Reached only after OTP/SSO auth; MainLayout bounces any
 * app-route visit back here while the gate is armed. There is no skip —
 * completing the step or logging out are the only exits.
 */
const MpinPage = ({ history }: RouteComponentProps) => {
    const styles = useMpinStyles();
    const {
        variant,
        context,
        mpinLength,
        resolving,
        values,
        errors,
        submitting,
        attemptsRemaining,
        isBlocked,
        secondsLeft,
        successTitle,
        forgotOpen,
        exitConfirmOpen,
        canSubmit,
        userName,
        handleFieldChange,
        handleSubmit,
        openForgot,
        closeForgot,
        confirmExit,
        cancelExit,
        handleLogout,
    } = useMpin(history);

    // No renderable variant: the mount guard is redirecting (no token /
    // NOT_APPLICABLE) — paint nothing rather than a broken card.
    if (!variant || !context) {
        return (
            <Box className={styles.page}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const attemptsCaption =
        !isBlocked && attemptsRemaining !== null && attemptsRemaining > 0
            ? `${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining`
            : null;

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && canSubmit) {
            handleSubmit();
        }
    };

    return (
        <Box className={styles.page}>
            <Box className={styles.card} onKeyDown={onKeyDown}>
                {resolving && (
                    <LinearProgress
                        className={styles.resolveBar}
                        color="primary"
                    />
                )}

                {successTitle ? (
                    <MpinSuccessTick title={successTitle} />
                ) : (
                    <>
                        <Box className={styles.brandRow}>
                            {variant.showGreeting && userName ? (
                                <Box className={styles.avatar}>
                                    {initialsOf(userName)}
                                </Box>
                            ) : (
                                <Box className={styles.iconBadge}>
                                    <LockOutlinedIcon />
                                </Box>
                            )}
                        </Box>
                        <Typography className={styles.title}>
                            {variant.title(firstNameOf(userName))}
                        </Typography>
                        <Typography className={styles.subtitle}>
                            {variant.subtitle}
                        </Typography>

                        {!!context.message && !isBlocked && (
                            <MpinBanner message={context.message} />
                        )}

                        {isBlocked ? (
                            <>
                                <LockoutPanel secondsLeft={secondsLeft} />
                                {!!variant.forgotAfterField && (
                                    <Button
                                        disableRipple
                                        className={styles.forgotLink}
                                        onClick={openForgot}
                                    >
                                        Forgot PIN?
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                {variant.fields.map((field, index) => (
                                    <Box key={field.key}>
                                        <MpinCellInput
                                            label={field.label}
                                            value={values[field.key] ?? ''}
                                            onChange={handleFieldChange(
                                                field.key,
                                            )}
                                            length={mpinLength}
                                            hasError={!!errors[field.key]}
                                            errorMessage={errors[field.key]}
                                            disabled={submitting}
                                            autoFocus={index === 0}
                                        />
                                        {variant.forgotAfterField ===
                                            field.key && (
                                            <Button
                                                disableRipple
                                                className={styles.forgotLink}
                                                onClick={openForgot}
                                            >
                                                Forgot PIN?
                                            </Button>
                                        )}
                                    </Box>
                                ))}

                                {!!errors.form && (
                                    <Box className={styles.formError}>
                                        <ErrorOutlineIcon
                                            className={styles.formErrorIcon}
                                        />
                                        <Typography
                                            className={styles.formErrorText}
                                        >
                                            {errors.form}
                                        </Typography>
                                    </Box>
                                )}
                                {/* {!!attemptsCaption && (
                                    <Typography
                                        className={styles.attemptsCaption}
                                    >
                                        {attemptsCaption}
                                    </Typography>
                                )} */}

                                <Button
                                    className={styles.cta}
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    disabled={!canSubmit}
                                    onClick={handleSubmit}
                                >
                                    {submitting ? (
                                        <CircularProgress
                                            size={22}
                                            style={{ color: '#fff' }}
                                        />
                                    ) : (
                                        variant.cta
                                    )}
                                </Button>
                            </>
                        )}

                        <Box className={styles.footer}>
                            <Button
                                disableRipple
                                className={styles.footerLink}
                                onClick={handleLogout}
                            >
                                Log in with a different account
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
            <ForgotMpinDialog open={forgotOpen} onClose={closeForgot} />
            <ExitMpinDialog
                open={exitConfirmOpen}
                onConfirm={confirmExit}
                onCancel={cancelExit}
            />
        </Box>
    );
};

export default withRouter(MpinPage);
